import * as Sentry from '@sentry/browser';
import { format } from 'date-fns';
import posthog from 'posthog-js';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { DatePicker } from '@/components/common/DatePicker';
import SubmitButton from '@/components/common/SubmitButton';
import type { ModalProps } from '@/components/providers/ModalProvider/ModalProvider';
import { Field, FieldLabel } from '@/components/shadcn/field';
import { Input } from '@/components/shadcn/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/shadcn/select';
import { Switch } from '@/components/shadcn/switch';
import useAnvaEmployees from '@/features/crm/queries/hooks/useAnvaEmployees';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import type { Party } from '@/features/party/types';
import useAuth from '@/hooks/useAuth';

import useActionsAnva from '../../hooks/useActionsAnva';
import useEnsureEmailDocument from '../../hooks/useEnsureEmailDocument';
import useOnlyRelevantAnvaPolicyRef from '../../hooks/useOnlyRelevantAnvaPolicyRef';
import AssigneeField from '../AssigneeField';

import AnvaPolicyField from './AnvaPolicyField';
import type { ExportEmailValuesAnva } from './types';

const CRM_KEY = 'anva';

type Props = ModalProps & {
  defaultValues?: Partial<ExportEmailValuesAnva>;
  party: Maybe<Party>;
};

export default function ExportEmailModalAnva({
  onClose,
  defaultValues,
  party,
}: Props) {
  const { t } = useTranslation();
  const { integrations } = useAuth();

  const ensureEmailDocument = useEnsureEmailDocument();

  const { data: employees } = useAnvaEmployees();

  const brokerAnvaEmployee = employees?.find(
    (employee) => employee.value === integrations?.anva_preferred_agent_nr,
  );

  const onlyRelevantPolicyRef = useOnlyRelevantAnvaPolicyRef(party);

  const {
    register,
    handleSubmit,
    control,
    formState: { isSubmitting, isValid },
    watch,
  } = useForm<ExportEmailValuesAnva>({
    mode: 'onChange',
    shouldUnregister: true,
    defaultValues: {
      createTask: true,
      title: defaultValues?.title,
      date: defaultValues?.date ?? new Date(),
      assignee: brokerAnvaEmployee,
      segmentCode: '11',
      crmPolicyId: defaultValues?.crmPolicyId ?? onlyRelevantPolicyRef,
    },
  });

  const crmPartyId = party?.external_refs.find(
    (ref) => ref.company.toLowerCase() === CRM_KEY,
  )?.ref;

  const anvaActions = useActionsAnva();
  const { getSelectedMessageId } = useEmailClient();

  const onSubmit: SubmitHandler<ExportEmailValuesAnva> = async (data) => {
    try {
      if (!crmPartyId) {
        throw new Error('Missing crmPartyId when exporting to anva');
      }

      const { filename, link } = await ensureEmailDocument();

      await anvaActions.uploadDocument({
        task: data.createTask
          ? {
              description: data.title,
              dueDate: format(data.date, 'yyyy-MM-dd'),
              assignedTo: data.assignee?.value,
            }
          : undefined,
        description: data.title,
        crmPartyId,
        crmPolicyId: data.crmPolicyId,
        segmentCode: data.segmentCode,
        filename,
        link,
      });

      posthog.capture('Email exported to CRM', {
        crmBackend: CRM_KEY,
        partyId: party?.id,
        messageId: getSelectedMessageId(),
        withTask: Boolean(data.createTask),
        withPolicy: Boolean(data.crmPolicyId),
      });
      toast.success(t('EMAIL_EXPORT_SUCCESS'));

      onClose();
    } catch (error) {
      toast.error(t('EMAIL_EXPORT_ERROR'));
      Sentry.captureException(error);
    }
  };

  const createTask = watch('createTask');

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 p-2">
      <div className="flex flex-col gap-2">
        <div className="mb-3">
          <Controller
            control={control}
            name="createTask"
            render={({ field }) => (
              <Field orientation="horizontal">
                <Switch
                  name="createTask"
                  id="createTask"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />

                <FieldLabel htmlFor="createTask" className="cursor-pointer">
                  {t('EXPORT_EMAIL_CREATE_TASK_LABEL')}
                </FieldLabel>
              </Field>
            )}
          />
        </div>

        <Field>
          <FieldLabel htmlFor="title">{t('TASK_FIELD_TITLE')}</FieldLabel>
          <Input
            {...register('title', { required: true })}
            placeholder={t('TASK_FIELD_TITLE_PH')}
          />
        </Field>

        <AnvaPolicyField control={control} party={party} />

        {createTask && (
          <>
            <Controller
              control={control}
              name="date"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{t('TASK_FIELD_DUE_DATE')}</FieldLabel>
                  <DatePicker {...field} />
                </Field>
              )}
            />

            <AssigneeField control={control} employees={employees} />
          </>
        )}

        <Controller
          control={control}
          name="segmentCode"
          render={({ field }) => (
            <Field>
              <FieldLabel>
                {t('EXPORT_EMAIL_ANVA_SEGMENT_CODE_LABEL')}
              </FieldLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={'11'}>
                    {t('EXPORT_EMAIL_ANVA_SEGMENT_CODE_11')}
                  </SelectItem>

                  <SelectItem value={'12'}>
                    {t('EXPORT_EMAIL_ANVA_SEGMENT_CODE_12')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}
        />
      </div>

      <div className="flex justify-end">
        <SubmitButton isSubmitting={isSubmitting} disabled={!isValid}>
          {createTask
            ? t('EXPORT_EMAIL_AND_CREATE_TASK_SUBMIT_BUTTON')
            : t('EXPORT_EMAIL_SUBMIT_BUTTON')}
        </SubmitButton>
      </div>
    </form>
  );
}
